/**
 * GameplayPillsPanel — admin controls for the TWO info pills displayed on
 * every game screen (Treinar · Competição · VS Tempo · VS Robot · VS Humano):
 *   • timer   → the clock pill with the elapsed time
 *   • level   → the challenge/level pill (e.g. "5º + 6º (Nível 2)")
 *
 * Fields per pill: font size, font color, background, border, weight, text
 * alignment, uppercase, vertical offset (px), shadow toggle + optional PT/EN/ES
 * label prefix (empty = no prefix). Persisted under `homepage_config.gameplay_pills`.
 */
import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { setPath } from "./setPath.js";
import { LANGS } from "./shared.jsx";

const ALIGN_OPTIONS = [
  { value: "left",   label: "◧ Esquerda" },
  { value: "center", label: "◫ Centro" },
  { value: "right",  label: "◨ Direita" },
];

function PillControls({ cfg, setCfg, base, title, accent, defaults, testPrefix, showSubColor = false }) {
  const s = cfg.gameplay_pills?.[base] || {};
  const set = (field, value) => setCfg(setPath(cfg, ["gameplay_pills", base, field], value));
  const setLabel = (lang, value) => setCfg(setPath(cfg, ["gameplay_pills", base, "label", lang], value));
  const v = (field) => (s[field] === undefined ? defaults[field] : s[field]);

  return (
    <div className={`mt-3 rounded-lg border p-3 space-y-3 ${accent}`}>
      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-700">🎯 {title}</div>

      {/* Cores + tamanhos */}
      <div className="grid sm:grid-cols-2 gap-2">
        <div className="flex items-center gap-2">
          <Label className="text-[10px] w-20">Cor do texto</Label>
          <Input type="color" value={v("font_color")} onChange={(e) => set("font_color", e.target.value)} className="h-8 w-14 p-1" data-testid={`${testPrefix}-font-color`} />
          <Input value={v("font_color")} onChange={(e) => set("font_color", e.target.value)} className="h-8 text-xs" />
        </div>
        {showSubColor && (
          <div className="flex items-center gap-2">
            <Label className="text-[10px] w-20">Cor secundária</Label>
            <Input type="color" value={v("sub_color")} onChange={(e) => set("sub_color", e.target.value)} className="h-8 w-14 p-1" data-testid={`${testPrefix}-sub-color`} />
            <Input value={v("sub_color")} onChange={(e) => set("sub_color", e.target.value)} className="h-8 text-xs" />
          </div>
        )}
        <div className="flex items-center gap-2">
          <Label className="text-[10px] w-20">Fundo</Label>
          <Input type="color" value={v("bg_color")} onChange={(e) => set("bg_color", e.target.value)} className="h-8 w-14 p-1" data-testid={`${testPrefix}-bg-color`} />
          <Input value={v("bg_color")} onChange={(e) => set("bg_color", e.target.value)} className="h-8 text-xs" />
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-[10px] w-20">Borda</Label>
          <Input type="color" value={v("border_color")} onChange={(e) => set("border_color", e.target.value)} className="h-8 w-14 p-1" data-testid={`${testPrefix}-border-color`} />
          <Input value={v("border_color")} onChange={(e) => set("border_color", e.target.value)} className="h-8 text-xs" />
        </div>
        <div>
          <Label className="text-[10px]">Tamanho da fonte ({v("font_size")}px)</Label>
          <Input type="range" min={10} max={40} value={v("font_size")} onChange={(e) => set("font_size", Number(e.target.value))} data-testid={`${testPrefix}-font-size`} />
        </div>
        <div>
          <Label className="text-[10px]">Peso ({v("weight")})</Label>
          <Input type="range" min={100} max={900} step={100} value={v("weight")} onChange={(e) => set("weight", Number(e.target.value))} data-testid={`${testPrefix}-weight`} />
        </div>
        <div>
          <Label className="text-[10px]">Alinhamento</Label>
          <select
            value={v("text_align")}
            onChange={(e) => set("text_align", e.target.value)}
            className="w-full h-8 text-xs rounded border border-slate-200 px-2 bg-white"
            data-testid={`${testPrefix}-text-align`}
          >
            {ALIGN_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-[10px]">Posição vertical ({v("offset_y")}px)</Label>
          <Input type="range" min={-40} max={40} step={1} value={v("offset_y")} onChange={(e) => set("offset_y", Number(e.target.value))} data-testid={`${testPrefix}-offset-y`} />
        </div>
      </div>

      {/* Toggles */}
      <div className="flex flex-wrap items-center gap-4 text-xs">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!v("uppercase")} onChange={(e) => set("uppercase", e.target.checked)} data-testid={`${testPrefix}-uppercase`} />
          <span>Maiúsculas</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={v("shadow") !== false} onChange={(e) => set("shadow", e.target.checked)} data-testid={`${testPrefix}-shadow`} />
          <span>Sombra</span>
        </label>
      </div>

      {/* Prefix multilíngua (opcional) */}
      <div className="space-y-1">
        <Label className="text-[11px] font-bold text-slate-700">
          Prefixo opcional antes do valor (vazio = sem prefixo)
        </Label>
        <div className="grid sm:grid-cols-3 gap-2">
          {LANGS.map(({ code, flag }) => (
            <Input
              key={code}
              value={s.label?.[code] || ""}
              onChange={(e) => setLabel(code, e.target.value)}
              placeholder={`${flag} ex: ${base === "timer" ? "Tempo:" : "Desafio:"}`}
              data-testid={`${testPrefix}-label-${code}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export function GameplayPillsPanel({ cfg, setCfg }) {
  return (
    <div className="space-y-3" data-testid="gameplay-pills-panel">
      <p className="text-[11px] text-slate-500">
        Duas caixas informativas mostradas em <b>todos os ecrãs de jogo</b> (Treinar · Competição · VS Tempo ·
        VS Robot · VS Humano). Cada uma tem controlo independente de fonte, cor, fundo, borda, alinhamento e
        posição vertical, além de um prefixo opcional em PT/EN/ES.
      </p>
      <PillControls
        cfg={cfg}
        setCfg={setCfg}
        base="timer"
        title="Cronómetro (relógio)"
        accent="border-sky-200 bg-sky-50/40"
        defaults={{
          font_size: 20, font_color: "#0F172A", sub_color: "#64748B",
          bg_color: "#FFFFFF", border_color: "#FFFFFF",
          weight: 700, text_align: "left", uppercase: false,
          offset_y: 0, shadow: true,
        }}
        testPrefix="pills-timer"
        showSubColor
      />
      <PillControls
        cfg={cfg}
        setCfg={setCfg}
        base="level"
        title="Nível / Desafio"
        accent="border-amber-200 bg-amber-50/40"
        defaults={{
          font_size: 16, font_color: "#0F172A",
          bg_color: "#FFFFFF", border_color: "#FFFFFF",
          weight: 700, text_align: "center", uppercase: false,
          offset_y: 0, shadow: true,
        }}
        testPrefix="pills-level"
      />
    </div>
  );
}
